/**
 * "Importer fra video" — writer-only entry point for the video recipe import
 * flow (video-import, Slice 4; API contract from Slices 1-3). A cook video
 * (a YouTube link, a direct video url, or an uploaded .mp4/.mov/.webm file)
 * is sent to the AI understanding layer and comes back as ONE unsaved,
 * editor-ready DRAFT — same REVIEW-BEFORE-SAVE contract as
 * `PhotoImportButton`/`UrlImportForm`: this component NEVER persists a
 * recipe itself, it only hands the draft to `onImported`, which the caller
 * uses to navigate into the editor.
 *
 * PREMIUM, UP FRONT: video import is a paid capability, so the toggle refuses
 * BEFORE the modal opens — i.e. before the user pastes a link or picks a file
 * — when the active cookbook has no Premium capabilities (`isPremium` — the
 * OWNER's plan governs the cookbook, §2.1). Letting the user hunt down a video
 * and only THEN saying "that needs Premium" reads as a failure (issue #19).
 * The toggle stays VISIBLE and enabled on purpose — the user should learn the
 * feature exists — it just answers with the same calm `PremiumNotice` the
 * server's 402 produces. The 402 handling below REMAINS: this gate is UX, not
 * a security boundary, and the plan can change between page load and click.
 *
 * UI shape: a small MODAL (not an inline reveal like `UrlImportForm`) — video
 * import has more surface (two mutually-exclusive source modes, an optional
 * pasted-ingredients field, a multi-phase progress readout, and a
 * failure-specific "try a file instead" action), so a modal keeps the
 * sjef-bar itself uncluttered. Two source modes, toggled by the user (never
 * both, never neither):
 *  - "url": paste a public video link (YouTube or a direct video file link).
 *  - "file": pick a local .mp4/.mov/.webm file — validated client-side
 *    (`validateVideoFile`) before any upload is attempted, then uploaded to a
 *    presigned URL exactly like the photo-import flow (`requestVideoImportUpload`
 *    → PUT → `startVideoImport({ uploadedKey })`).
 * An optional "Lim inn ingredienser" textarea threads through as
 * `ingredientText` regardless of source mode.
 *
 * Progress: video extraction can take a while (acquisition + a multimodal AI
 * call), so while the job is `pending` we surface the server's own advisory
 * `phase` (via `pollImportJob`'s `onProgress`) as a short Norwegian label.
 *
 * Errors: the usual `UnauthorizedError`/`ForbiddenError`/`NotFoundError`/
 * `ImportPollTimeoutError` mapping (mirrors `PhotoImportButton`). A failed job
 * (`ImportJobFailedError`) shows the server's own `serverMessage`, and — when
 * the server also sent an `errorWorkaround` — that workaround prominently,
 * plus (for the two failure codes whose workaround IS "upload a file
 * instead": `LOGIN_REQUIRED`, `NOT_A_VIDEO`) a one-click action that switches
 * the form to file mode so the user doesn't have to re-read instructions.
 */

import type { RecipeImportDraft } from "@tjommi/shared";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  EntitlementRequiredError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  requestVideoImportUpload,
  startVideoImport,
} from "../api";
import {
  DEFAULT_MAX_POLL_ATTEMPTS,
  DEFAULT_POLL_INTERVAL_MS,
  ImportJobFailedError,
  ImportPollCancelledError,
  ImportPollTimeoutError,
  pollImportJob,
} from "../lib/importPoll";
import { resolveVideoContentType, validateVideoFile } from "../lib/videoUpload";
import { PremiumNotice } from "./PremiumNotice";
import { type EntitlementDenial, isEntitlementDenial } from "../lib/entitlement";

type VideoSourceMode = "url" | "file";

/** Failure codes whose server-provided workaround is "upload the video as a
 * file instead" — offer the one-click mode switch only for these. */
const FILE_FALLBACK_ERROR_CODES = new Set(["LOGIN_REQUIRED", "NOT_A_VIDEO"]);

/** A cheap client-side format check — non-empty and parses as an http(s) url
 * (mirrors `UrlImportForm`'s own check; the real validation is server-side). */
function looksLikeHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/** Norwegian label for the video job's advisory `phase` (video-import only —
 * `null`/absent while the job hasn't reported one yet). */
function phaseLabel(phase: string | null | undefined): string {
  switch (phase) {
    case "acquiring":
      return "Henter video …";
    case "processing":
      return "Tolker video …";
    case "structuring":
      return "Setter sammen oppskrift …";
    default:
      return "Importerer video …";
  }
}

export function VideoImportButton({
  cookbookId,
  isPremium,
  accessToken,
  onSessionExpired,
  onImported,
  toggleClassName = "sjef-import-btn",
  toggleLabel = "Importer fra video",
  testId = "video-import",
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
  onBusyChange,
}: {
  cookbookId: string;
  /**
   * Whether the ACTIVE COOKBOOK has Premium capabilities
   * (`CookbookSummary.isPremium` from `/me`, via the cookbook context). NOT
   * "is the caller premium" — §2.1: the owner's plan governs the cookbook.
   * Required (no default) so a new call site cannot silently skip the gate.
   * A UI hint only — the server re-checks and answers 402.
   */
  isPremium: boolean;
  accessToken: string;
  onSessionExpired: () => void;
  /** The caller navigates into the editor with this draft (review-before-save). */
  onImported: (draft: RecipeImportDraft) => void;
  toggleClassName?: string;
  toggleLabel?: React.ReactNode;
  testId?: string;
  /** Injectable for tests — how often to poll the job status. */
  pollIntervalMs?: number;
  /** Injectable for tests — how many polls before giving up client-side. */
  maxPollAttempts?: number;
  /** Reports whether an import is in flight, so a host sheet can refuse to
   * close (and so discard it) meanwhile. */
  onBusyChange?: (busy: boolean) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<VideoSourceMode>("url");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [ingredientText, setIngredientText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!busy) return;
    onBusyChange?.(true);
    return () => onBusyChange?.(false);
  }, [busy]);
  const [phase, setPhase] = useState<string | null>(null);
  // A string (the usual short inline error) OR a denial — either the parsed
  // 402 or the client's own up-front `isPremium` refusal. One state keeps them
  // mutually exclusive.
  const [error, setError] = useState<string | EntitlementDenial | null>(null);
  const [errorWorkaround, setErrorWorkaround] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Same race-safety pattern as PhotoImportButton/UrlImportForm: a run token,
  // checked before acting on an async result, so an unmount or a newer submit
  // never lets a stale poll loop call setState/onImported after the fact.
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * A cookbook switch must not leave the previous cookbook's answer on screen.
   * `HomePage` keeps the SAME component instance across a switch (no `key`),
   * so without this the Premium notice from a Free cookbook would still sit
   * under a button that now works. `isPremium` is in the deps too: a `/me`
   * refresh that flips the plan is the same situation.
   */
  useEffect(() => {
    setError(null);
  }, [cookbookId, isPremium]);

  /**
   * The ONE way the modal is opened. Without Premium capabilities we never
   * open it — so the user is never asked to paste a link or pick a file for an
   * action that was never available — and answer with the same notice as the
   * 402 instead.
   */
  function openModal(): void {
    if (!isPremium) {
      setError({ code: "video_import" });
      return;
    }
    setError(null);
    setOpen(true);
  }

  function resetForm(): void {
    setMode("url");
    setUrl("");
    setFile(null);
    setIngredientText("");
    setError(null);
    setErrorWorkaround(null);
    setErrorCode(null);
    setPhase(null);
  }

  function closeModal(): void {
    setOpen(false);
    resetForm();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = ""; // allow re-picking the same file
    if (!picked) return;
    setError(null);
    setErrorWorkaround(null);
    setErrorCode(null);
    const validationError = validateVideoFile(picked);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }
    setFile(picked);
  }

  const useFileInstead = useCallback(() => {
    setMode("file");
    setError(null);
    setErrorWorkaround(null);
    setErrorCode(null);
  }, []);

  const doImport = useCallback(async (): Promise<void> => {
    if (busy) return;
    setError(null);
    setErrorWorkaround(null);
    setErrorCode(null);

    if (mode === "url" && !looksLikeHttpUrl(url)) {
      setError("Ugyldig lenke.");
      return;
    }
    if (mode === "file" && !file) {
      setError("Velg en videofil.");
      return;
    }

    const runId = ++runIdRef.current;
    const isActive = () => mountedRef.current && runIdRef.current === runId;
    setBusy(true);
    setPhase(null);
    try {
      const trimmedIngredientText = ingredientText.trim() || undefined;
      let jobId: string;
      if (mode === "file") {
        const chosen = file!;
        const contentType = resolveVideoContentType(chosen);
        if (!contentType) {
          if (isActive()) setError("Filtypen støttes ikke. Bruk MP4, MOV eller WebM.");
          return;
        }
        const { key, uploadUrl } = await requestVideoImportUpload(
          cookbookId,
          contentType,
          accessToken,
        );
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: chosen,
          headers: { "Content-Type": contentType },
        });
        if (!putRes.ok) {
          if (isActive()) setError("Opplasting feilet. Prøv igjen.");
          return;
        }
        ({ jobId } = await startVideoImport(
          cookbookId,
          { uploadedKey: key, ingredientText: trimmedIngredientText },
          accessToken,
        ));
      } else {
        ({ jobId } = await startVideoImport(
          cookbookId,
          { url: url.trim(), ingredientText: trimmedIngredientText },
          accessToken,
        ));
      }

      const draft = await pollImportJob(cookbookId, jobId, accessToken, {
        intervalMs: pollIntervalMs,
        maxAttempts: maxPollAttempts,
        isActive,
        onProgress: (status) => {
          if (isActive()) setPhase(status.phase ?? null);
        },
      });
      if (isActive()) {
        onImported(draft);
        closeModal();
      }
    } catch (err) {
      if (err instanceof ImportPollCancelledError) return; // a newer submit / unmount
      if (err instanceof UnauthorizedError) return onSessionExpired();
      if (err instanceof ForbiddenError) return setError("Ingen skrivetilgang.");
      // 402: video import isn't in the cookbook's plan (either the upload-url
      // call or the start call can answer this). NOT the same as the 403 above.
      if (err instanceof EntitlementRequiredError) return setError(err);
      if (err instanceof NotFoundError)
        return setError("Importen kunne ikke finnes igjen. Prøv igjen.");
      if (err instanceof ImportJobFailedError) {
        setError(err.serverMessage ?? "Klarte ikke å lese oppskriften fra videoen.");
        setErrorWorkaround(err.errorWorkaround ?? null);
        setErrorCode(err.errorCode ?? null);
        return;
      }
      if (err instanceof ImportPollTimeoutError)
        return setError("Importen tok for lang tid. Prøv igjen.");
      setError(err instanceof Error ? err.message : "Noe gikk galt. Prøv igjen.");
    } finally {
      if (mountedRef.current && runIdRef.current === runId) {
        setBusy(false);
        setPhase(null);
      }
    }
  }, [
    accessToken,
    busy,
    cookbookId,
    file,
    ingredientText,
    maxPollAttempts,
    mode,
    onImported,
    onSessionExpired,
    pollIntervalMs,
    url,
  ]);

  const showFileFallbackAction =
    mode !== "file" && errorCode != null && FILE_FALLBACK_ERROR_CODES.has(errorCode);

  if (!open) {
    return (
      <>
        <button type="button" className={toggleClassName} data-testid={testId} onClick={openModal}>
          {toggleLabel}
        </button>
        {/* The up-front refusal lives next to the button that was pressed —
            same component, same words as the server's 402. */}
        {isEntitlementDenial(error) && (
          <PremiumNotice denial={error} testId={`${testId}-premium`} />
        )}
      </>
    );
  }

  return (
    <div className="video-import-modal-backdrop" data-testid={`${testId}-backdrop`}>
      <div className="video-import-modal" data-testid={`${testId}-modal`}>
        <h2>Importer fra video</h2>
        <div className="video-import-tabs" role="tablist" aria-label="Videokilde">
          <button
            type="button"
            className={`video-import-tab${mode === "url" ? " active" : ""}`}
            role="tab"
            aria-selected={mode === "url"}
            data-testid={`${testId}-mode-url`}
            disabled={busy}
            onClick={() => setMode("url")}
          >
            Lenke
          </button>
          <button
            type="button"
            className={`video-import-tab${mode === "file" ? " active" : ""}`}
            role="tab"
            aria-selected={mode === "file"}
            data-testid={`${testId}-mode-file`}
            disabled={busy}
            onClick={() => setMode("file")}
          >
            Last opp fil
          </button>
        </div>

        {mode === "url" ? (
          <input
            type="url"
            className="video-import-input"
            placeholder="Lim inn lenke til en video …"
            aria-label="Lenke til video"
            data-testid={`${testId}-url-input`}
            value={url}
            disabled={busy}
            onChange={(e) => setUrl(e.target.value)}
          />
        ) : (
          <>
            <button
              type="button"
              className="video-import-file-pick"
              data-testid={`${testId}-file-pick`}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {file ? file.name : "Velg videofil (MP4, MOV, WebM)"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              className="visually-hidden"
              data-testid={`${testId}-file-input`}
              onChange={handleFileChange}
            />
          </>
        )}

        <textarea
          className="video-import-ingredients"
          placeholder="Lim inn ingredienser (valgfritt)"
          aria-label="Lim inn ingredienser"
          data-testid={`${testId}-ingredients`}
          value={ingredientText}
          disabled={busy}
          onChange={(e) => setIngredientText(e.target.value)}
        />

        {busy && (
          <p className="video-import-progress" data-testid={`${testId}-progress`}>
            {phaseLabel(phase)}
          </p>
        )}

        {isEntitlementDenial(error) ? (
          <PremiumNotice denial={error} testId={`${testId}-premium`} />
        ) : typeof error === "string" ? (
          <div className="video-import-error-block">
            <p className="image-error" data-testid={`${testId}-error`} role="alert">
              {error}
            </p>
            {errorWorkaround && (
              <p className="video-import-workaround" data-testid={`${testId}-workaround`}>
                {errorWorkaround}
              </p>
            )}
            {showFileFallbackAction && (
              <button
                type="button"
                className="btn btn-ghost"
                data-testid={`${testId}-use-file-instead`}
                onClick={useFileInstead}
              >
                Last opp fil i stedet
              </button>
            )}
          </div>
        ) : null}

        <div className="video-import-actions">
          <button
            type="button"
            className="btn btn-primary"
            data-testid={`${testId}-submit`}
            disabled={busy}
            onClick={() => void doImport()}
          >
            {busy ? "Importerer …" : "Importer"}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid={`${testId}-cancel`}
            disabled={busy}
            onClick={closeModal}
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}
